import React from "react";
import {useFormContext} from "react-hook-form";
import {Box, Colors, Text} from "@bounce/shared/ui";
import {PaymentMethodDisplayFlow} from "../../../../apis/requests/payment-flows/types";
import {Button} from "../../../../ui/components";
import FormSection from "../../flow-form/FormSection";
import {findDefaultFlow} from "../../flow-form/helpers";
import {FromFeatureFlagChip, VariantCardHeader} from "../../flow-form/VariantCardParts";
import {getDefaultControlDisplayPolicy} from "./helpers";
import MethodList from "./MethodList";
import {FormValues} from "./schema";

const NO_FLAG_TEXT = "Select a feature flag to load its variants.";
const VARIANTS_HELP =
  "For each variant, set which payment methods checkout shows, in what order, and which one is preselected. A method the user can't use is skipped.";
const METHODS_TITLE = "Payment methods:";
const RESET_LABEL = "Reset to default - control variant";

type VariantCardProps = {
  index: number;
  defaultFlow: PaymentMethodDisplayFlow | undefined;
};

const VariantCard = ({index, defaultFlow}: VariantCardProps) => {
  const {watch, setValue} = useFormContext<FormValues>();
  const variant = watch(`variants.${index}`);

  const resetToDefaultControl = () =>
    setValue(`variants.${index}.policy`, getDefaultControlDisplayPolicy(defaultFlow), {shouldDirty: true});

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        border: `1px solid ${Colors.tertiary._300}`,
        borderRadius: "10px",
        backgroundColor: Colors.bw.white,
      }}
    >
      <VariantCardHeader index={index} name={variant.name} weight={variant.weight} />
      <Box
        sx={{display: "flex", flexDirection: "column", gap: 1, borderTop: `1px solid ${Colors.tertiary._300}`, pt: 1.5}}
      >
        <Text size={14} variant="interDisplay600" sx={{color: Colors.grey._900}}>
          {METHODS_TITLE}
        </Text>
        <MethodList variantIndex={index} />
        <Box>
          <Button buttonType="text" size="small" onClick={resetToDefaultControl}>
            {RESET_LABEL}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const VariantsSection = ({flows}: {flows: PaymentMethodDisplayFlow[]}) => {
  const {watch} = useFormContext<FormValues>();
  const featureFlag = watch("feature_flag");
  const variants = watch("variants");
  const defaultFlow = findDefaultFlow(flows);

  return (
    <FormSection
      title="Variants"
      help={featureFlag ? VARIANTS_HELP : NO_FLAG_TEXT}
      action={featureFlag && <FromFeatureFlagChip />}
    >
      {variants.map((variant, index) => (
        <VariantCard key={`${featureFlag}-${variant.name}`} index={index} defaultFlow={defaultFlow} />
      ))}
    </FormSection>
  );
};

export default VariantsSection;
